package fr.frux.di

import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.remember
import fr.frux.core.ads.LocalAdsController
import fr.frux.core.common.platform.donationsAllowed
import fr.frux.feature.auth.AuthViewModel
import fr.frux.feature.auth.AuthViewModelFactory
import fr.frux.feature.auth.LocalAuthViewModelFactory
import fr.frux.feature.blockedusers.BlockedUsersViewModel
import fr.frux.feature.blockedusers.BlockedUsersViewModelFactory
import fr.frux.feature.blockedusers.LocalBlockedUsersViewModelFactory
import fr.frux.feature.favorites.FavoritesViewModel
import fr.frux.feature.favorites.FavoritesViewModelFactory
import fr.frux.feature.favorites.LocalFavoritesViewModelFactory
import fr.frux.feature.feedback.FeedbackViewModel
import fr.frux.feature.feedback.FeedbackViewModelFactory
import fr.frux.feature.feedback.LocalFeedbackViewModelFactory
import fr.frux.feature.harvestcreate.CreateHarvestViewModel
import fr.frux.feature.harvestcreate.CreateHarvestViewModelFactory
import fr.frux.feature.harvestcreate.LocalCreateHarvestViewModelFactory
import fr.frux.feature.harvestdetail.HarvestDetailViewModel
import fr.frux.feature.harvestdetail.HarvestDetailViewModelFactory
import fr.frux.feature.harvestdetail.LocalHarvestDetailViewModelFactory
import fr.frux.feature.harvests.HarvestsViewModel
import fr.frux.feature.harvests.HarvestsViewModelFactory
import fr.frux.feature.harvests.LocalHarvestsViewModelFactory
import fr.frux.feature.map.LocalMapViewModelFactory
import fr.frux.feature.map.MapViewModel
import fr.frux.feature.map.MapViewModelFactory
import fr.frux.feature.map.MapViewStore
import fr.frux.feature.map.NeverSeenInviteStore
import fr.frux.feature.map.site.LocalSiteLayerViewModelFactory
import fr.frux.feature.map.site.SiteLayerViewModel
import fr.frux.feature.map.site.SiteLayerViewModelFactory
import fr.frux.feature.mytrees.LocalMyTreesViewModelFactory
import fr.frux.feature.mytrees.MyTreesViewModel
import fr.frux.feature.mytrees.MyTreesViewModelFactory
import fr.frux.feature.notifications.LocalNotificationPreferencesViewModelFactory
import fr.frux.feature.notifications.LocalNotificationsViewModelFactory
import fr.frux.feature.notifications.NotificationPreferencesViewModel
import fr.frux.feature.notifications.NotificationPreferencesViewModelFactory
import fr.frux.feature.notifications.NotificationsViewModel
import fr.frux.feature.notifications.NotificationsViewModelFactory
import fr.frux.feature.premium.LocalPremiumViewModelFactory
import fr.frux.feature.premium.PremiumViewModel
import fr.frux.feature.premium.PremiumViewModelFactory
import fr.frux.feature.premium.PurchaseLauncher
import fr.frux.feature.profile.ContributionNudgeStore
import fr.frux.feature.profile.LocalProfileViewModelFactory
import fr.frux.feature.profile.ProfileOptions
import fr.frux.feature.profile.ProfileViewModel
import fr.frux.feature.profile.ProfileViewModelFactory
import fr.frux.feature.site.create.CreateSiteViewModel
import fr.frux.feature.site.create.CreateSiteViewModelFactory
import fr.frux.feature.site.create.LocalCreateSiteViewModelFactory
import fr.frux.feature.site.detail.LocalSiteDetailViewModelFactory
import fr.frux.feature.site.detail.SiteDetailViewModel
import fr.frux.feature.site.detail.SiteDetailViewModelFactory
import fr.frux.feature.support.LocalSupportPromptViewModelFactory
import fr.frux.feature.support.SupportPromptViewModel
import fr.frux.feature.support.SupportPromptViewModelFactory
import fr.frux.feature.treeadd.FirstContributionCheck
import fr.frux.feature.treeadd.TreeAddIntroStore
import fr.frux.feature.treeadd.wizard.LocalTreeWizardViewModelFactory
import fr.frux.feature.treeadd.wizard.TreeWizardViewModel
import fr.frux.feature.treeadd.wizard.TreeWizardViewModelFactory
import fr.frux.feature.treedetail.LocalTreeDetailViewModelFactory
import fr.frux.feature.treedetail.TreeDetailEditing
import fr.frux.feature.treedetail.TreeDetailViewModel
import fr.frux.feature.treedetail.TreeDetailViewModelFactory
import fr.frux.feature.treedetail.move.LocalMoveTreeViewModelFactory
import fr.frux.feature.treedetail.move.MoveTreeViewModel
import fr.frux.feature.treedetail.move.MoveTreeViewModelFactory
import fr.frux.feature.treelist.LocalTreeListViewModelFactory
import fr.frux.feature.treelist.TreeListViewModel
import fr.frux.feature.treelist.TreeListViewModelFactory
import fr.frux.feature.userprofile.LocalUserProfileViewModelFactory
import fr.frux.feature.userprofile.UserProfileViewModel
import fr.frux.feature.userprofile.UserProfileViewModelFactory
import fr.frux.navigation.consumeInitialMapView
import fr.frux.shared.di.AppComponent
import fr.frux.shared.domain.session.SessionState
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.merge

/**
 * Composition root de l'application : le seul endroit qui voit à la fois le graphe Metro et les
 * ViewModels des features (#43).
 *
 * Il remplace l'ancien service locator global `AppComponentProvider` : le graphe entre par
 * paramètre depuis chaque point d'entrée de plateforme, et descend sous forme de fabriques typées.
 * Une feature ne connaît plus que son propre contrat de fabrique — elle ne peut plus atteindre le
 * graphe, ni un dépôt qu'elle n'a pas déclaré.
 *
 * L'assemblage est écrit à la main, délibérément : le graphe vit dans `:shared`, sous les features,
 * donc Metro ne peut pas voir leurs ViewModels pour en générer les fabriques (l'inverse créerait
 * un cycle de modules). Cette liste est le prix — et la carte lisible — de cette frontière.
 */
@Composable
fun FruxCompositionRoot(
    component: AppComponent,
    // Achat Frux+ (#457) : par défaut celui du graphe, présent sur Android seulement. Paramètre
    // gardé pour que les tests substituent leur propre lanceur.
    purchaseLauncher: PurchaseLauncher? = rememberStorePurchaseLauncher(component),
    content: @Composable () -> Unit,
) {
    // `remember(component)` et non un appel direct : les locals sont statiques, une nouvelle
    // instance de fabrique à chaque recomposition invaliderait tout le sous-arbre.
    val authFactory =
        remember(component) {
            AuthViewModelFactory { AuthViewModel(sessionStore = component.getSessionStore()) }
        }
    val mapFactory =
        remember(component) {
            MapViewModelFactory { areaRequested ->
                MapViewModel(
                    treeRepository = component.getTreeRepository(),
                    speciesRepository = component.getSpeciesRepository(),
                    placeRepository = component.getPlaceRepository(),
                    locationProvider = component.getLocationProvider(),
                    viewStore = MapViewStore(component.getAppPreferences(), initialView = ::consumeInitialMapView),
                    favoritesStore = component.getFavoritesStore(),
                    sessionStore = component.getSessionStore(),
                    neverSeenInviteStore = NeverSeenInviteStore(component.getAppPreferences()),
                    contentVisibilityChanges =
                        merge(
                            component.getUserRepository().blockChanges,
                            component.getTreeCorrectionRepository().applied.map { },
                            // Une observation de terrain change le marqueur : lot rechargé au retour (#648).
                            component.getTreeRepository().statusObserved.map { },
                        ),
                    adsController = component.getAdsController(),
                    areaRequested = areaRequested,
                    clusterPyramidRepository = component.getClusterPyramidRepository(),
                )
            }
        }
    val siteLayerFactory =
        remember(component) {
            SiteLayerViewModelFactory { SiteLayerViewModel(component.getSiteRepository()) }
        }
    val siteDetailFactory =
        remember(component) {
            SiteDetailViewModelFactory { siteId ->
                SiteDetailViewModel(
                    siteId = siteId,
                    siteRepository = component.getSiteRepository(),
                    speciesRepository = component.getSpeciesRepository(),
                    harvestRepository = component.getHarvestRepository(),
                    // Provider, pas un snapshot (#264) : même raison que treeDetailFactory.
                    currentUserId = {
                        (component.getSessionStore().state.value as? SessionState.Authenticated)?.user?.id
                    },
                )
            }
        }
    val createSiteFactory =
        remember(component) {
            CreateSiteViewModelFactory { args ->
                CreateSiteViewModel(
                    args = args,
                    siteRepository = component.getSiteRepository(),
                    speciesRepository = component.getSpeciesRepository(),
                )
            }
        }
    val treeWizardFactory =
        remember(component) {
            TreeWizardViewModelFactory { start ->
                TreeWizardViewModel(
                    treeRepository = component.getTreeRepository(),
                    speciesRepository = component.getSpeciesRepository(),
                    photoUploads = component.getTreePhotoUploads(),
                    start = start,
                    introStore = TreeAddIntroStore(component.getAppPreferences()),
                    isFirstContribution =
                        FirstContributionCheck(
                            userRepository = component.getUserRepository(),
                            currentUserId = {
                                (component.getSessionStore().state.value as? SessionState.Authenticated)?.user?.id
                            },
                        )::isFirst,
                )
            }
        }
    val treeListFactory =
        remember(component) {
            TreeListViewModelFactory { origin ->
                TreeListViewModel(
                    treeRepository = component.getTreeRepository(),
                    speciesRepository = component.getSpeciesRepository(),
                    origin = origin,
                )
            }
        }
    val treeDetailFactory =
        remember(component) {
            TreeDetailViewModelFactory { treeId ->
                TreeDetailViewModel(
                    treeId = treeId,
                    treeRepository = component.getTreeRepository(),
                    speciesRepository = component.getSpeciesRepository(),
                    favoritesStore = component.getFavoritesStore(),
                    harvestRepository = component.getHarvestRepository(),
                    // Provider, pas un snapshot (#264) : cette fiche est désormais ouverte à
                    // l'anonyme, et le ViewModel construit avant connexion survit à un login
                    // déclenché depuis sa propre invite (Nav3 réutilise le ViewModelStore de
                    // l'entrée à la reprise post-`onAuthenticated()`). Le patron "snapshot #129
                    // lot B" qui tenait encore ici ne suffit plus : un `Long?` figé resterait
                    // `null` après connexion et re-poserait l'invite indéfiniment.
                    currentUserIdProvider = {
                        (component.getSessionStore().state.value as? SessionState.Authenticated)?.user?.id
                    },
                    userRepository = component.getUserRepository(),
                    editing =
                        TreeDetailEditing(
                            component.getTreeCorrectionRepository(),
                            component.getTreePhotoUploads(),
                            sites = component.getSiteRepository(),
                        ),
                )
            }
        }
    val moveTreeFactory =
        remember(component) {
            MoveTreeViewModelFactory { args ->
                MoveTreeViewModel(args, component.getTreeCorrectionRepository(), component.getTreeRepository())
            }
        }
    val harvestDetailFactory =
        remember(component) {
            HarvestDetailViewModelFactory { harvestId ->
                HarvestDetailViewModel(
                    harvestId = harvestId,
                    harvestRepository = component.getHarvestRepository(),
                    treeRepository = component.getTreeRepository(),
                    siteRepository = component.getSiteRepository(),
                    // Provider, pas un snapshot (#264) : même raison que treeDetailFactory
                    // ci-dessus — la fiche récolte est aussi ouverte à l'anonyme, et "Rejoindre"
                    // peut être suivi d'un login qui réutilise ce même ViewModel.
                    currentUserIdProvider = {
                        (component.getSessionStore().state.value as? SessionState.Authenticated)?.user?.id
                    },
                    userRepository = component.getUserRepository(),
                )
            }
        }
    val userProfileFactory =
        remember(component) {
            UserProfileViewModelFactory { userId ->
                UserProfileViewModel(
                    userId = userId,
                    userRepository = component.getUserRepository(),
                    // Provider, pas un snapshot (#264) : même raison que treeDetailFactory.
                    currentUserIdProvider = {
                        (component.getSessionStore().state.value as? SessionState.Authenticated)?.user?.id
                    },
                )
            }
        }
    val createHarvestFactory =
        remember(component) {
            CreateHarvestViewModelFactory { target ->
                CreateHarvestViewModel(
                    target = target,
                    harvestRepository = component.getHarvestRepository(),
                    treeRepository = component.getTreeRepository(),
                    siteRepository = component.getSiteRepository(),
                )
            }
        }
    val feedbackFactory =
        remember(component) {
            FeedbackViewModelFactory { sourceScreen ->
                FeedbackViewModel(sourceScreen = sourceScreen, repository = component.getFeedbackRepository())
            }
        }
    // Sans lanceur (web, desktop, iOS, build de dev), l'écran n'affiche que l'état.
    val premiumFactory =
        remember(component, purchaseLauncher) {
            PremiumViewModelFactory {
                PremiumViewModel(repository = component.getPremiumRepository(), purchaseLauncher = purchaseLauncher)
            }
        }
    val blockedUsersFactory =
        remember(component) {
            BlockedUsersViewModelFactory { BlockedUsersViewModel(userRepository = component.getUserRepository()) }
        }
    val profileFactory =
        remember(component) {
            ProfileViewModelFactory {
                ProfileViewModel(
                    sessionStore = component.getSessionStore(),
                    authRepository = component.getAuthRepository(),
                    fileExporter = component.getFileExporter(),
                    notificationBadgeStore = component.getNotificationBadgeStore(),
                    pushRegistrar = component.getPushRegistrar(),
                    userRepository = component.getUserRepository(),
                    supportRepository = component.getSupportRepository(),
                    favoritesStore = component.getFavoritesStore(),
                    options =
                        ProfileOptions(
                            donationsAllowed = donationsAllowed,
                            nudgeStore = ContributionNudgeStore(component.getAppPreferences()),
                        ),
                    adsController = component.getAdsController(),
                )
            }
        }
    val notificationsFactory =
        remember(component) {
            NotificationsViewModelFactory {
                NotificationsViewModel(
                    notificationRepository = component.getNotificationRepository(),
                    badgeStore = component.getNotificationBadgeStore(),
                )
            }
        }
    val notificationPreferencesFactory =
        remember(component) {
            NotificationPreferencesViewModelFactory {
                NotificationPreferencesViewModel(
                    notificationRepository = component.getNotificationRepository(),
                    pushRegistrar = component.getPushRegistrar(),
                )
            }
        }
    val favoritesFactory =
        remember(component) {
            FavoritesViewModelFactory {
                FavoritesViewModel(
                    treeRepository = component.getTreeRepository(),
                    locationProvider = component.getLocationProvider(),
                    speciesRepository = component.getSpeciesRepository(),
                    favoritesStore = component.getFavoritesStore(),
                    siteRepository = component.getSiteRepository(),
                )
            }
        }
    val harvestsFactory =
        remember(component) {
            HarvestsViewModelFactory { mapCamera, recenterMap ->
                HarvestsViewModel(
                    harvestRepository = component.getHarvestRepository(),
                    speciesRepository = component.getSpeciesRepository(),
                    placeRepository = component.getPlaceRepository(),
                    mapCamera = mapCamera,
                    recenterMap = recenterMap,
                )
            }
        }
    val myTreesFactory =
        remember(component) {
            MyTreesViewModelFactory {
                MyTreesViewModel(
                    treeRepository = component.getTreeRepository(),
                    speciesRepository = component.getSpeciesRepository(),
                )
            }
        }
    val supportPromptFactory =
        remember(component) {
            SupportPromptViewModelFactory {
                SupportPromptViewModel(
                    supportRepository = component.getSupportRepository(),
                    donationsAllowed = donationsAllowed,
                )
            }
        }

    // Construire le contrôleur l'active (#527) : il suit dès lors le premier plan et les droits. Le
    // hold d'onboarding d'`App` est posé dans la même composition, avant sa première lecture.
    val adsController = remember(component) { component.getAdsController() }

    CompositionLocalProvider(
        LocalAdsController provides adsController,
        LocalAuthViewModelFactory provides authFactory,
        LocalMapViewModelFactory provides mapFactory,
        LocalSiteLayerViewModelFactory provides siteLayerFactory,
        LocalSiteDetailViewModelFactory provides siteDetailFactory,
        LocalCreateSiteViewModelFactory provides createSiteFactory,
        LocalTreeWizardViewModelFactory provides treeWizardFactory,
        LocalTreeListViewModelFactory provides treeListFactory,
        LocalTreeDetailViewModelFactory provides treeDetailFactory,
        LocalMoveTreeViewModelFactory provides moveTreeFactory,
        LocalHarvestDetailViewModelFactory provides harvestDetailFactory,
        LocalCreateHarvestViewModelFactory provides createHarvestFactory,
        LocalFeedbackViewModelFactory provides feedbackFactory,
        LocalPremiumViewModelFactory provides premiumFactory,
        LocalBlockedUsersViewModelFactory provides blockedUsersFactory,
        LocalProfileViewModelFactory provides profileFactory,
        LocalUserProfileViewModelFactory provides userProfileFactory,
        LocalFavoritesViewModelFactory provides favoritesFactory,
        LocalHarvestsViewModelFactory provides harvestsFactory,
        LocalMyTreesViewModelFactory provides myTreesFactory,
        LocalNotificationsViewModelFactory provides notificationsFactory,
        LocalNotificationPreferencesViewModelFactory provides notificationPreferencesFactory,
        LocalSupportPromptViewModelFactory provides supportPromptFactory,
        content = content,
    )
}

/** Lanceur adossé à l'achat en store du graphe, `null` là où aucun store n'est branché. */
@Composable
private fun rememberStorePurchaseLauncher(component: AppComponent): PurchaseLauncher? =
    remember(component) {
        component.getStorePurchases().takeIf { it.isAvailable }?.let { store -> PurchaseLauncher { store.purchase() } }
    }
